import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import schoolLogo from '../school-brand.jpg';
import * as Styles from './styles';

function FAQMobile() {
  const loginTopRef = useRef(null);
  const [openIndex, setOpenIndex] = useState(null);

  // Quick Login Drawer State
  const [showQuickLogin, setShowQuickLogin] = useState(false);
  const [quickEmail, setQuickEmail] = useState('');
  const [quickPassword, setQuickPassword] = useState('');

  // Forgot Password States
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  const handleQuickLoginClick = () => {
    setShowQuickLogin(true);
    setIsForgotMode(false);
    setForgotMessage('');
    if (loginTopRef.current) {
      loginTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    try {
      localStorage.removeItem('instructorId');
      localStorage.removeItem('learnerId');
      localStorage.removeItem('userId');
      localStorage.removeItem('adminToken');

      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: quickEmail, password: quickPassword })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('needsPasswordReset', data.needsPasswordReset ? 'true' : 'false');

        if (data.role === 'admin') {
          localStorage.setItem('userRole', 'admin');
          localStorage.setItem('adminToken', data.token || 'mock-admin-token-secure');
          window.location.assign('/admin');
        } else if (data.role === 'instructor') {
          localStorage.setItem('userRole', 'instructor');
          localStorage.setItem('instructorId', data.id || data.instructorId);
          localStorage.setItem('token', data.token || '');
          window.location.assign('/instructor-dash');
        } else {
          localStorage.setItem('userRole', 'learner');
          localStorage.setItem('userId', data.id || data.userId);
          localStorage.setItem('learnerId', data.id || data.userId);
          localStorage.setItem('userName', data.name || "Learner Driver");
          localStorage.setItem('token', data.token || '');
          window.location.assign(`/learner-dash/${data.instructor_id || 5}`);
        }
      } else {
        alert(data.error || "Login failed.");
      }
    } catch (err) {
      alert("Server error.");
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (res.ok) {
        setForgotMessage('Password reset link sent! Check your email.');
      } else {
        setForgotMessage(data.error || 'Could not send reset link.');
      }
    } catch (err) {
      setForgotMessage('Server error. Please try again.');
    }
  };

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = [
    {
      q: "How do I sign up as a driving instructor?",
      a: "Click 'Register' in the top navigation or 'Get Started' on the homepage and create your instructor account. Once your account is set up, you can access your dashboard and begin adding and managing your own students."
    },
    {
      q: "Do you provide learners to instructors?",
      a: "We do not directly provide learners, but we offer our registered instructors great local exposure by featuring on our public homepage map, connecting you with potential students looking for lessons in your area."
    },
    {
      q: "How do I add a student?",
      a: "From your instructor dashboard, you can add your own students by entering their details, including their email address. Once added, the student will receive an invitation or account setup link so they can access their own Lesson-Lane account."
    },
    {
      q: "What happens when I add a student?",
      a: "When you add a student and provide their email address, Lesson-Lane can send them an invitation or account setup link. Once they activate their account, they can access the student features available to them and connect with your instructor account."
    },
    {
      q: "What can my students see?",
      a: "Students have their own account where they can see information relating to their lessons. They can view your available lesson times, book lessons, see upcoming sessions, view their lesson history and follow their learning progress."
    },
    {
      q: "Can my students book their own lessons?",
      a: "Yes. Once you have added a student and they have activated their account, they can view the lesson times you have made available and book a suitable slot. Once booked, the lesson is added to the shared schedule."
    },
    {
      q: "Can students see my live availability?",
      a: "Yes. Students can view the availability you make available through your instructor calendar. Booked or unavailable times are not shown as available booking slots."
    },
    {
      q: "What happens when a student books a lesson?",
      a: "The booking is added to the instructor's calendar and the student's upcoming lessons. This keeps both accounts up to date and helps prevent double bookings."
    },
    {
      q: "Can I control when students can book?",
      a: "Yes. Instructors manage their own availability through the dashboard. You control which times are available for students to book."
    },
    {
      q: "Can I message my students?",
      a: "Yes. Lesson-Lane includes messaging within the instructor dashboard, allowing you to communicate directly with the students you have added to your account. Students can view and respond to messages from their own account."
    },
    {
      q: "Can I track my students' progress?",
      a: "Yes. Lesson-Lane allows instructors to keep track of student progress and lesson information through the dashboard. Students can also view relevant progress information from their own account."
    },
    {
      q: "Does Lesson-Lane provide my students to other instructors?",
      a: "No. Your students are managed through your own instructor account. Lesson-Lane does not operate a shared student pool or allocate your students to other instructors."
    },
    {
      q: "Does Lesson-Lane have access to my student list?",
      a: "Lesson-Lane provides the platform and tools you use to manage your students, calendar, bookings, messages and progress. Student relationships remain managed through the individual instructor accounts and are not used to allocate students to other instructors."
    },
    {
      q: "What happens if I need to cancel or reschedule a lesson?",
      a: "Lesson arrangements are managed between the instructor and student. Your calendar allows you to manage lesson sessions and availability. Any cancellation or rescheduling should follow your own instructor policy and the arrangements agreed with your student."
    },
    {
      q: "Is Lesson-Lane suitable for manual and automatic instructors?",
      a: "Yes. Lesson-Lane is designed to support driving instructors who provide manual or automatic car training. The platform helps instructors manage their students, calendar, bookings, communication and progress."
    },
    {
      q: "What happens to my data if I delete my account?",
      a: "When an account is deleted, associated data will be permanently deleted in accordance with our data retention and deletion procedures. We recommend downloading or retaining any information you may need before deleting your account, as deleted information may not be recoverable."
    },
    {
      q: "Who is responsible for my students?",
      a: "The instructor remains responsible for their own students and teaching relationship. This includes lesson arrangements, communication, teaching and any professional or legal responsibilities that apply to the instructor's services."
    },
    {
      q: "Who is responsible for DVSA requirements and insurance?",
      a: "Independent driving instructors are responsible for maintaining any required DVSA approvals, badges, qualifications and appropriate insurance for the services they provide. Lesson-Lane is a management platform and does not replace these responsibilities."
    }
  ];

  return (
    <div style={{ ...Styles.containerStyle, minHeight: '100vh', background: '#0f172a', color: '#ffffff', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      
      {/* --- TOP ANCHOR REF FOR QUICK LOGIN --- */}
      <div ref={loginTopRef} />

      {/* --- UNIFORM APP HEADER --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#090d16', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={schoolLogo} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #3b82f6' }} />
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })} style={{ color: '#fff', fontWeight: '800', fontSize: '1.1rem', letterSpacing: '-0.5px', textDecoration: 'none' }}>LessonLane</Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })} title="Home" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '6px 10px', borderRadius: '8px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="#FFFFFF"/>
            </svg>
          </Link>
          <button 
            onClick={handleQuickLoginClick}
            title="Portal Login"
            style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C9.24 2 7 4.24 7 7C7 9.42 8.74 11.43 11 11.9V13.1C7.58 13.54 5 16.48 5 20H19C19 16.48 16.42 13.54 13 13.1V11.9C15.26 11.43 17 9.42 17 7C17 4.24 14.76 2 12 2Z" fill="#FFFFFF"/>
            </svg>
          </button>
        </div>
      </div>

      {/* --- QUICK LOGIN / FORGOT PASSWORD EXPANDABLE BOX --- */}
      {showQuickLogin && (
        <div style={{ padding: '16px 16px 0 16px', maxWidth: '500px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          {!isForgotMode ? (
            <form onSubmit={handleQuickLogin} style={{ background: '#162238', padding: '18px', borderRadius: '14px', border: '1.5px solid #3b82f6', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#ffffff', fontWeight: '800' }}>Portal Sign In</h4>
                <button type="button" onClick={() => setShowQuickLogin(false)} style={{ background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
              </div>
              <input 
                type="email" 
                placeholder="Email address" 
                value={quickEmail} 
                onChange={e => setQuickEmail(e.target.value)} 
                required 
                style={{ width: '100%', padding: '10px 12px', marginBottom: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }}
              />
              <input 
                type="password" 
                placeholder="Password" 
                value={quickPassword} 
                onChange={e => setQuickPassword(e.target.value)} 
                required 
                style={{ width: '100%', padding: '10px 12px', marginBottom: '6px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }}
              />
              <div style={{ textAlign: 'right', marginBottom: '10px' }}>
                <button 
                  type="button"
                  onClick={() => { setIsForgotMode(true); setForgotMessage(''); }}
                  style={{ background: 'none', border: 'none', fontSize: '11.5px', color: '#60a5fa', textDecoration: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Forgot password?
                </button>
              </div>
              <button type="submit" style={{ width: '100%', padding: '10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                Sign In ➔
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPasswordSubmit} style={{ background: '#162238', padding: '18px', borderRadius: '14px', border: '1.5px solid #3b82f6', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#ffffff', fontWeight: '800' }}>Reset Password</h4>
                <button type="button" onClick={() => setShowQuickLogin(false)} style={{ background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
              </div>
              <p style={{ fontSize: '11.5px', color: '#94a3b8', marginBottom: '10px', lineHeight: '1.4' }}>
                Enter your account email and we'll send you a recovery link.
              </p>
              <input 
                type="email" 
                placeholder="Enter your email address" 
                value={forgotEmail} 
                onChange={e => setForgotEmail(e.target.value)} 
                required 
                style={{ width: '100%', padding: '10px 12px', marginBottom: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }}
              />
              {forgotMessage && (
                <div style={{ fontSize: '11.5px', color: forgotMessage.includes('sent') ? '#10b981' : '#f87171', marginBottom: '8px' }}>
                  {forgotMessage}
                </div>
              )}
              <button type="submit" style={{ width: '100%', padding: '10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>
                Send Reset Link ➔
              </button>
              <div style={{ textAlign: 'center' }}>
                <button 
                  type="button" 
                  onClick={() => setIsForgotMode(false)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div style={{ padding: '30px 16px 50px', maxWidth: '650px', margin: '0 auto', width: '100%', boxSizing: 'border-box', flex: 1 }}>
        
        {/* HEADER SECTION */}
        <div style={{ textAlign: 'center', marginBottom: '25px' }}>
          <div style={{ color: '#60a5fa', fontSize: '0.72rem', fontWeight: '800', letterSpacing: '1px', marginBottom: '6px' }}>
            HELP & INFORMATION
          </div>
          <h1 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: '900', margin: '0 0 8px', letterSpacing: '-0.5px', color: '#ffffff' }}>
            Frequently Asked Questions
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: '1.5', maxWidth: '480px', margin: '0 auto' }}>
            Everything you need to know about using Lesson-Lane to manage your students, lessons, and calendar.
          </p>
        </div>

        {/* FAQ ACCORDION LIST */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '35px' }}>
          {faqData.map((item, idx) => {
            const isOpen = openIndex === idx;

            return (
              <div
                key={idx}
                onClick={() => toggleAccordion(idx)}
                style={{
                  background: '#162238',
                  border: isOpen ? '1.5px solid #3b82f6' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  padding: '16px 18px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', fontWeight: '700', fontSize: '0.92rem', color: '#ffffff' }}>
                  <span>{item.q}</span>
                  <span style={{ color: '#60a5fa', fontSize: '1.1rem', flexShrink: 0 }}>
                    {isOpen ? '−' : '+'}
                  </span>
                </div>

                {isOpen && (
                  <p style={{ marginTop: '10px', color: '#94a3b8', lineHeight: '1.6', fontSize: '0.84rem', marginBottom: 0 }}>
                    {item.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* CONTACT SUPPORT CTA */}
        <div style={{ textAlign: 'center', background: '#162238', padding: '25px 20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '800', margin: '0 0 6px', color: '#ffffff' }}>
            Still have questions?
          </h3>
          <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '0.82rem' }}>
            Our support team is always here to help you get started.
          </p>
          <Link to="/contact" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
            <button style={{ padding: '10px 20px', background: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '0.84rem', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>
              Contact Support
            </button>
          </Link>
        </div>

        {/* --- TERMS & CONDITIONS LINK AT THE BOTTOM --- */}
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
          Read our official <Link to="/terms" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })} style={{ color: '#60a5fa', fontWeight: '600', textDecoration: 'underline' }}>Terms & Conditions</Link>
        </div>

      </div>

    </div>
  );
}

export default FAQMobile;